'use client';
import { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';

const specialties = [
  'Médecine générale',
  'Cardiologie',
  'Endocrinologie – Diabétologie – Nutrition',
  'Pédiatrie',
  'Dermatologie',
  'Gynécologie',
  'Radiologie',
  'Gastro-entérologie',
  'Gériatrie',
  'Hématologie',
  'Infectiologie',
  'Neurologie',
  'Néphrologie',
  'ORL',
  'Médecine du travail',
  'Ophtalmologie',
  'Pneumologie',
  'Rhumatologie',
  'Stomatologie',
  'Urologie',
  'Urgences médicales',
];

const timeSlots = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30',
];

interface FormData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  birthDate: string;
  gender: string;
  specialty: string;
  date: string;
  time: string;
  reason: string;
  insurance: string;
  isFirstVisit: string;
}

export default function RendezVousPage() {
  const [form, setForm] = useState<FormData>({
    firstName: '', lastName: '', phone: '', email: '',
    birthDate: '', gender: '', specialty: '', date: '',
    time: '', reason: '', insurance: '', isFirstVisit: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState(1);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const nextStep = () => setStep(s => Math.min(s + 1, 3));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  const isStep1Valid = form.firstName && form.lastName && form.phone && form.gender;
  const isStep2Valid = form.specialty && form.date && form.time;

  if (submitted) {
    return (
      <main>
        <Header />
        <section className="py-32 bg-white">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="w-24 h-24 rounded-full flex items-center justify-center text-4xl text-white mx-auto mb-8" style={{ backgroundColor: '#0e5932' }}>
              <i className="fas fa-check"></i>
            </div>
            <h2 className="text-3xl font-black mb-4" style={{ color: '#1d3c5e', fontFamily: 'Lato, sans-serif' }}>Rendez-vous Confirmé !</h2>
            <p className="text-gray-600 mb-4 leading-relaxed">
              Votre demande de rendez-vous en <strong>{form.specialty}</strong> a bien été enregistrée pour le <strong>{form.date}</strong> à <strong>{form.time}</strong>.
            </p>
            <p className="text-gray-500 text-sm mb-8">Un SMS de confirmation vous sera envoyé au <strong>{form.phone}</strong>. Notre équipe vous contactera pour confirmer votre rendez-vous.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/" className="px-8 py-3 rounded-xl font-bold text-white text-sm" style={{ backgroundColor: '#0e5932' }}>
                Retour à l'Accueil
              </Link>
              <button onClick={() => { setSubmitted(false); setStep(1); setForm({ firstName: '', lastName: '', phone: '', email: '', birthDate: '', gender: '', specialty: '', date: '', time: '', reason: '', insurance: '', isFirstVisit: '' }); }} className="px-8 py-3 rounded-xl font-bold text-sm" style={{ backgroundColor: '#fbaf45', color: '#1d3c5e' }}>
                Nouveau Rendez-vous
              </button>
            </div>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <Header />

      {/* HERO */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #0e5932 0%, #0a3f24 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 text-center">
          <span className="text-xs font-bold uppercase tracking-widest mb-3 block" style={{ color: '#fbaf45' }}>Consultation</span>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4" style={{ fontFamily: 'Lato, sans-serif' }}>Prendre un Rendez-vous</h1>
          <p className="text-white opacity-80 max-w-2xl mx-auto">Réservez votre consultation avec l'un de nos spécialistes en quelques étapes simples.</p>
          <div className="flex items-center justify-center gap-2 mt-6 text-sm text-white opacity-60">
            <Link href="/" className="hover:opacity-100">Accueil</Link>
            <span>/</span>
            <span>Prendre un Rendez-vous</span>
          </div>
        </div>
      </section>

      {/* FORM SECTION */}
      <section className="py-20" style={{ backgroundColor: '#f8fdf9' }}>
        <div className="max-w-4xl mx-auto px-4">

          {/* STEP INDICATOR */}
          <div className="flex items-center justify-center mb-12">
            {[
              { num: 1, label: 'Vos Informations' },
              { num: 2, label: 'Votre Consultation' },
              { num: 3, label: 'Confirmation' },
            ].map((s, i) => (
              <div key={i} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-black text-sm transition-all"
                    style={{
                      backgroundColor: step >= s.num ? '#0e5932' : '#e5e7eb',
                      color: step >= s.num ? '#fff' : '#9ca3af',
                    }}
                  >
                    {step > s.num ? <i className="fas fa-check text-xs"></i> : s.num}
                  </div>
                  <span className="text-xs font-semibold mt-2 hidden sm:block" style={{ color: step >= s.num ? '#0e5932' : '#9ca3af' }}>
                    {s.label}
                  </span>
                </div>
                {i < 2 && (
                  <div className="w-16 sm:w-24 h-0.5 mx-2 mb-5" style={{ backgroundColor: step > s.num ? '#0e5932' : '#e5e7eb' }}></div>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">

              {/* STEP 1: Personal Info */}
              {step === 1 && (
                <div>
                  <h2 className="text-2xl font-black mb-8" style={{ color: '#1d3c5e', fontFamily: 'Lato, sans-serif' }}>
                    <i className="fas fa-user mr-3" style={{ color: '#0e5932' }}></i>
                    Vos Informations Personnelles
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold mb-2" style={{ color: '#1d3c5e' }}>Prénom *</label>
                      <input name="firstName" value={form.firstName} onChange={handleChange} required placeholder="Votre prénom" className="w-full px-4 py-3 rounded-xl border-2 text-sm outline-none transition-all" style={{ borderColor: form.firstName ? '#0e5932' : '#e5e7eb' }} />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2" style={{ color: '#1d3c5e' }}>Nom *</label>
                      <input name="lastName" value={form.lastName} onChange={handleChange} required placeholder="Votre nom de famille" className="w-full px-4 py-3 rounded-xl border-2 text-sm outline-none transition-all" style={{ borderColor: form.lastName ? '#0e5932' : '#e5e7eb' }} />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2" style={{ color: '#1d3c5e' }}>Téléphone *</label>
                      <input name="phone" value={form.phone} onChange={handleChange} required placeholder="+225 00 00 00 00" type="tel" className="w-full px-4 py-3 rounded-xl border-2 text-sm outline-none transition-all" style={{ borderColor: form.phone ? '#0e5932' : '#e5e7eb' }} />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2" style={{ color: '#1d3c5e' }}>Email</label>
                      <input name="email" value={form.email} onChange={handleChange} placeholder="votre@email.com" type="email" className="w-full px-4 py-3 rounded-xl border-2 text-sm outline-none transition-all" style={{ borderColor: form.email ? '#0e5932' : '#e5e7eb' }} />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2" style={{ color: '#1d3c5e' }}>Date de Naissance</label>
                      <input name="birthDate" value={form.birthDate} onChange={handleChange} type="date" className="w-full px-4 py-3 rounded-xl border-2 text-sm outline-none transition-all" style={{ borderColor: form.birthDate ? '#0e5932' : '#e5e7eb' }} />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2" style={{ color: '#1d3c5e' }}>Genre *</label>
                      <select name="gender" value={form.gender} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border-2 text-sm outline-none transition-all bg-white" style={{ borderColor: form.gender ? '#0e5932' : '#e5e7eb' }}>
                        <option value="">Sélectionner</option>
                        <option value="homme">Homme</option>
                        <option value="femme">Femme</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2" style={{ color: '#1d3c5e' }}>Assurance Santé</label>
                      <input name="insurance" value={form.insurance} onChange={handleChange} placeholder="Nom de votre assurance" className="w-full px-4 py-3 rounded-xl border-2 text-sm outline-none transition-all" style={{ borderColor: form.insurance ? '#0e5932' : '#e5e7eb' }} />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2" style={{ color: '#1d3c5e' }}>Première Visite ?</label>
                      <select name="isFirstVisit" value={form.isFirstVisit} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border-2 text-sm outline-none transition-all bg-white" style={{ borderColor: form.isFirstVisit ? '#0e5932' : '#e5e7eb' }}>
                        <option value="">Sélectionner</option>
                        <option value="oui">Oui, première visite</option>
                        <option value="non">Non, patient existant</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: Consultation */}
              {step === 2 && (
                <div>
                  <h2 className="text-2xl font-black mb-8" style={{ color: '#1d3c5e', fontFamily: 'Lato, sans-serif' }}>
                    <i className="fas fa-stethoscope mr-3" style={{ color: '#0e5932' }}></i>
                    Votre Consultation
                  </h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-bold mb-2" style={{ color: '#1d3c5e' }}>Spécialité Médicale *</label>
                      <select name="specialty" value={form.specialty} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border-2 text-sm outline-none transition-all bg-white" style={{ borderColor: form.specialty ? '#0e5932' : '#e5e7eb' }}>
                        <option value="">Choisir une spécialité</option>
                        {specialties.map((s, i) => <option key={i} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold mb-2" style={{ color: '#1d3c5e' }}>Date souhaitée *</label>
                        <input name="date" value={form.date} onChange={handleChange} required type="date" min={new Date().toISOString().split('T')[0]} className="w-full px-4 py-3 rounded-xl border-2 text-sm outline-none transition-all" style={{ borderColor: form.date ? '#0e5932' : '#e5e7eb' }} />
                      </div>
                      <div>
                        <label className="block text-sm font-bold mb-2" style={{ color: '#1d3c5e' }}>Heure souhaitée *</label>
                        <select name="time" value={form.time} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border-2 text-sm outline-none transition-all bg-white" style={{ borderColor: form.time ? '#0e5932' : '#e5e7eb' }}>
                          <option value="">Choisir un horaire</option>
                          {timeSlots.map((t, i) => <option key={i} value={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-2" style={{ color: '#1d3c5e' }}>Motif de la Consultation</label>
                      <textarea name="reason" value={form.reason} onChange={handleChange} rows={4} placeholder="Décrivez brièvement le motif de votre consultation (symptômes, suivi, bilan...)" className="w-full px-4 py-3 rounded-xl border-2 text-sm outline-none transition-all resize-none" style={{ borderColor: form.reason ? '#0e5932' : '#e5e7eb' }}></textarea>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: Confirmation */}
              {step === 3 && (
                <div>
                  <h2 className="text-2xl font-black mb-8" style={{ color: '#1d3c5e', fontFamily: 'Lato, sans-serif' }}>
                    <i className="fas fa-clipboard-check mr-3" style={{ color: '#0e5932' }}></i>
                    Confirmation de votre Rendez-vous
                  </h2>
                  <div className="rounded-2xl p-8 mb-8" style={{ backgroundColor: '#f8fdf9', border: '2px solid #0e593215' }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {[
                        ['Patient', `${form.firstName} ${form.lastName}`],
                        ['Téléphone', form.phone],
                        ['Email', form.email || 'Non renseigné'],
                        ['Genre', form.gender],
                        ['Spécialité', form.specialty],
                        ['Date', form.date],
                        ['Heure', form.time],
                        ['Assurance', form.insurance || 'Non renseignée'],
                        ['Première visite', form.isFirstVisit === 'oui' ? 'Oui' : form.isFirstVisit === 'non' ? 'Non' : 'Non précisé'],
                      ].map(([label, value], i) => (
                        <div key={i}>
                          <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: '#0e5932' }}>{label}</div>
                          <div className="text-sm font-semibold" style={{ color: '#1d3c5e' }}>{value}</div>
                        </div>
                      ))}
                    </div>
                    {form.reason && (
                      <div className="mt-6 pt-6" style={{ borderTop: '1px solid #0e593215' }}>
                        <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#0e5932' }}>Motif</div>
                        <div className="text-sm text-gray-600">{form.reason}</div>
                      </div>
                    )}
                  </div>
                  <div className="rounded-xl p-5 flex items-start gap-4" style={{ backgroundColor: '#fbaf4520', border: '1px solid #fbaf4540' }}>
                    <i className="fas fa-info-circle mt-0.5" style={{ color: '#fbaf45' }}></i>
                    <p className="text-sm text-gray-600">
                      En soumettant ce formulaire, vous acceptez d'être contacté par notre équipe pour confirmer votre rendez-vous. Un SMS de confirmation vous sera envoyé au numéro indiqué.
                    </p>
                  </div>
                </div>
              )}

              {/* NAVIGATION BUTTONS */}
              <div className="flex items-center justify-between mt-10 pt-8" style={{ borderTop: '1px solid #f0f0f0' }}>
                {step > 1 ? (
                  <button type="button" onClick={prevStep} className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all" style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}>
                    <i className="fas fa-arrow-left text-xs"></i> Précédent
                  </button>
                ) : <div></div>}

                {step < 3 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={step === 1 ? !isStep1Valid : !isStep2Valid}
                    className="flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-sm text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#0e5932' }}
                  >
                    Suivant <i className="fas fa-arrow-right text-xs"></i>
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90"
                    style={{ backgroundColor: '#0e5932' }}
                  >
                    <i className="fas fa-check mr-1"></i> Confirmer le Rendez-vous
                  </button>
                )}
              </div>
            </div>
          </form>

          {/* INFO BOXES */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
            {[
              { icon: 'fas fa-phone', title: 'Par Téléphone', desc: '+225 00 00 00 00', sub: 'Lun-Ven 8h-18h' },
              { icon: 'fas fa-map-marker-alt', title: 'Nous Trouver', desc: 'Yopougon Ananeraie', sub: 'Abidjan, Côte d\'Ivoire' },
              { icon: 'fas fa-clock', title: 'Horaires', desc: 'Lun-Ven 8h-19h', sub: 'Sam 8h-13h' },
            ].map((info, i) => (
              <div key={i} className="flex items-center gap-4 p-5 rounded-xl bg-white shadow-sm">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg flex-shrink-0" style={{ backgroundColor: '#0e593215', color: '#0e5932' }}>
                  <i className={info.icon}></i>
                </div>
                <div>
                  <div className="font-bold text-sm" style={{ color: '#1d3c5e' }}>{info.title}</div>
                  <div className="text-sm" style={{ color: '#0e5932' }}>{info.desc}</div>
                  <div className="text-xs text-gray-400">{info.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
